// Comptage d'un inventaire : référence de comparaison, écarts, état des thèmes, recherche d'objet.
// Décision 40 : on compare au dernier inventaire en date de l'emplacement, quelle que soit sa source ;
// sans inventaire précédent, au modèle. Décisions 24, 25, 26 et 36 pour les états et les raccourcis.

import { normalizeText } from './fieldModel'
import { formatShortDate } from './fieldDates'

// Nom exact du thème et de l'objet (aucune tolérance sur la casse ni les accents)
const keyOf = (themeName, objectName) => String(themeName).trim() + '|' + String(objectName).trim()

// Comptages du dernier inventaire par thème et par objet, s'il est comparable au modèle d'aujourd'hui :
// exactement les mêmes thèmes et objets (même nom), chacun avec la même quantité prévue, et un comptage
// pour chaque. Sinon null : le moindre écart de liste rend la comparaison trompeuse.
function comparableCounts(themes, lastInventory)
{
	if (!lastInventory || !Array.isArray(lastInventory.inventory))
		return null
	const expected = {}
	themes.forEach(theme => {
		theme.objects.forEach(object => {
			expected[keyOf(theme.name, object.name)] = object.expected
		})
	})
	const found = {}
	let objectsFound = 0
	for (const category of lastInventory.inventory)
	{
		for (const object of (category.objects || []))
		{
			const key = keyOf(category.name, object.name)
			const count = parseInt(object.count, 10)
			if (isNaN(count) || expected[key] === undefined || found[key] !== undefined || parseInt(object.quantity, 10) !== expected[key])
				return null
			found[key] = Math.max(0, count)
			objectsFound++
		}
	}
	return objectsFound > 0 && objectsFound === Object.keys(expected).length ? found : null
}

// Un inventaire enregistré : { type, create_time, inventory: [{ name, objects: [{ name, quantity, count }] }] }
export function buildReference(themes, lastInventory)
{
	const found = comparableCounts(themes, lastInventory)
	const counts = {}
	themes.forEach(theme => {
		theme.objects.forEach(object => {
			counts[object.id] = found ? found[keyOf(theme.name, object.name)] : object.expected
		})
	})
	return {
		source: found ? 'inventory' : 'model',
		id: found ? (lastInventory._id || null) : null,
		version: found ? (lastInventory.version || 1) : null,
		type: found ? lastInventory.type : null,
		time: found ? lastInventory.create_time : null,
		counts
	}
}

function referenceMoment(reference)
{
	if (reference.type === 'departure')
		return 'sortie'
	if (reference.type === 'arrival')
		return 'entrée'
	return 'inventaire'
}

// « entrée du 12 oct. »
export function referenceWhen(reference, now)
{
	return referenceMoment(reference) + ' du ' + formatShortDate(reference.time, now)
}

// « Comparé au dernier inventaire : entrée du 12 oct. »
export function referenceLabel(reference, now)
{
	if (reference.source !== 'inventory')
		return 'Comparé au modèle de l\'hébergement'
	return 'Comparé au dernier inventaire : ' + referenceWhen(reference, now)
}

// Ce qu'on affiche sous le nom d'un objet : référence, puis le modèle s'il diffère (décision 26)
export function objectReferenceLines(object, reference)
{
	const ref = reference.counts[object.id]
	if (reference.source !== 'inventory')
		return [object.expected + ' prévu' + (object.expected > 1 ? 's' : '')]
	let first = ref + ' au dernier inventaire'
	if (reference.type === 'arrival')
		first = ref + ' à l\'entrée'
	else if (reference.type === 'departure')
		first = ref + ' à la dernière sortie'
	if (ref === object.expected)
		return [first]
	return [first, object.expected + ' prévu' + (object.expected > 1 ? 's' : '')]
}

export function isMissingAtReference(object, reference)
{
	return reference.source === 'inventory' && reference.counts[object.id] < object.expected
}

// Comptage d'un objet : saisi, sinon la référence si le thème est validé, sinon rien
export function objectCount(object, counts, reference, themeDone)
{
	if (counts[object.id] !== undefined)
		return counts[object.id]
	if (themeDone)
		return reference.counts[object.id]
	return null
}

export function isGap(object, counts, reference)
{
	return counts[object.id] !== undefined && counts[object.id] !== reference.counts[object.id]
}

export function themeGaps(theme, counts, reference)
{
	return theme.objects.filter(object => isGap(object, counts, reference)).length
}

// État d'un thème (décision 24) : un écart se voit même si le thème n'est pas encore validé
export function themeState(theme, counts, reference, doneThemes)
{
	const gaps = themeGaps(theme, counts, reference)
	if (gaps > 0)
		return { state: 'gaps', gaps }
	if (doneThemes[theme.id])
		return { state: 'ok', gaps: 0 }
	return { state: 'todo', gaps: 0 }
}

export function progress(themes, doneThemes)
{
	const done = themes.filter(theme => doneThemes[theme.id]).length
	return { done, total: themes.length, left: themes.length - done }
}

export function totalGaps(themes, counts, reference)
{
	return themes.reduce((total, theme) => total + themeGaps(theme, counts, reference), 0)
}

// Thèmes que le raccourci global complète (décision 36) : ni validés, ni commencés par la personne
// (aucun comptage saisi). Un thème commencé mais pas validé reste à elle.
export function untouchedThemes(themes, counts, doneThemes)
{
	return themes.filter(theme => !doneThemes[theme.id] && theme.objects.every(object => counts[object.id] === undefined))
}

// Raccourci global : libellé et nombre d'objets concernés ; null s'il n'y a plus rien à compléter
export function confirmAllWording(themes, counts, reference, doneThemes)
{
	const targets = untouchedThemes(themes, counts, doneThemes)
	if (targets.length === 0)
		return null
	const count = targets.reduce((sum, theme) => sum + theme.objects.length, 0)
	const started = targets.length < themes.length
	const target = reference.source === 'inventory' ? 'comme au dernier inventaire' : 'conformes au modèle'
	if (!started)
		return { link: reference.source === 'inventory' ? 'Tout est comme au dernier inventaire' : 'Tout est conforme au modèle', count, target, others: false }
	return { link: reference.source === 'inventory' ? 'Tout le reste est comme au dernier inventaire' : 'Tout le reste est conforme au modèle', count, target, others: true }
}

// Recherche d'un objet dans tous les thèmes, accents et casse ignorés
export function searchObjects(themes, query)
{
	const needle = normalizeText(query)
	if (!needle)
		return []
	return themes
		.map(theme => ({ theme, objects: theme.objects.filter(object => normalizeText(object.name).includes(needle)) }))
		.filter(group => group.objects.length > 0)
}

export function plural(count, singular, pluralForm)
{
	return count + ' ' + (count > 1 ? (pluralForm || singular + 's') : singular)
}

// Pastille d'un objet déjà manquant au dernier inventaire (décision 26)
export function missingLabel(reference)
{
	if (reference.type === 'arrival')
		return 'Déjà absent à l\'entrée'
	if (reference.type === 'departure')
		return 'Déjà absent à la dernière sortie'
	return 'Déjà absent au dernier inventaire'
}

// Après « Enregistrer » dans un thème (décision 47) : le prochain thème pas encore fait,
// dans l'ordre du modèle, en reprenant au début si on en a sauté un. null : tout est fait.
export function nextTheme(themes, doneThemes, currentId)
{
	const start = themes.findIndex(theme => theme.id === currentId)
	for (let step = 1; step <= themes.length; step++)
	{
		const theme = themes[(start + step) % themes.length]
		if (theme.id !== currentId && !doneThemes[theme.id])
			return theme
	}
	return null
}
